import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Center,
  BigText,
  Description,
  Line,
  CleanLink,
  FilesContainer,
  FilesList,
  FileCard,
  FileInfo,
  FileThumbnail,
  FileDetails,
  FileName,
  FileType,
  DownloadButton
} from './styles';
import Icon from "../../../../../../components/Icon";
import FileIcon from "../../../../../../assets/icons/file-semibold.svg";
import DownloadIcon from "../../../../../../assets/icons/download-file.svg";
import { useTheme } from 'styled-components';
import { useDispatch, connect } from 'react-redux';
import { showModal } from '../../../../../../store/actions/modal';
import MediaModal from './components/MediaModal';
import constants from '../../../../../../constants';
import slugify from 'slugify';
import { SessionInDto } from '../../../../../../types';
import { AttachedFile } from '../../../../../../types/attached-file';
import { isFutureDate } from '../../../../../../helpers/dates-hanlder';
import { RemirrorPreview } from '../../../../Posts/Preview/components/PostRichTextPreview';

interface ComplementaryFilesProps {
  session: SessionInDto
  isMobile?: boolean;
  visibleModal?: string | null;
  groupId?: number;
}

const ComplementaryFiles: React.FC<ComplementaryFilesProps> = ({ session, isMobile = false, visibleModal, groupId }) => {
  const theme = useTheme() as { colors: { [key: string]: string } };
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  const isRecorded = !!session?.recorded_session_url && !isFutureDate(session?.session_datetime);

  const handleFileClick = useCallback((file: AttachedFile, index: number) => {
    // Check if it's a PDF - open in new tab
    if (file.mime === 'application/pdf') {
      if (groupId && file.id) {
        window.open(`/group/${groupId}/session/${session.id}/media/${file.id}`, '_blank');
      }

      return;
    }

    // Check if it's an image or video - open modal
    const isImage = constants.fileUploader.imagesMimes.includes(file.mime || '');
    const isVideo = constants.fileUploader.videoMimes.includes(file.mime || '');

    if (isImage || isVideo) {
      // Calculate the index in the filtered array (only images and videos)
      const filteredFiles = session?.complementary_material_files?.filter(f =>
        constants.fileUploader.imagesMimes.includes(f.mime || '') ||
        constants.fileUploader.videoMimes.includes(f.mime || '')
      );

      const filteredIndex = filteredFiles?.findIndex(f => {
        const matchById = f.id && file.id && f.id === file.id;
        const matchByUrl = f.url && file.url && f.url === file.url;
        const matchBySignedUrl = (f.signed_url || f.url) === (file.signed_url || file.url);
        return matchById || matchByUrl || matchBySignedUrl;
      });

      setSelectedFileIndex(filteredIndex !== undefined && filteredIndex >= 0 ? filteredIndex : 0);
      dispatch(showModal('complementary-files-modal'));
      return;
    }

    // For other file types, open in new tab
    window.open(file.signed_url || file.url, '_blank');
  }, [groupId, session?.id, session?.complementary_material_files, history, dispatch]);

  // Don't render if no complementary files
  if (!session?.complementary_material_files || session.complementary_material_files.length === 0) {
    return null;
  }

  const spacing = isMobile ? 35 : 35;
  const lineSpacing = isMobile ? 35 : 35;
  const titleSpacing = isMobile ? 14 : 17;
  const gapSpacing = isMobile ? 15 : 30;

  const getFileThumbnail = (file: AttachedFile) => {
    const isImage = constants.fileUploader.imagesMimes.includes(file.mime || '');
    const isVideo = constants.fileUploader.videoMimes.includes(file.mime || '');
    const isAudio = constants.fileUploader.audioMimes.includes(file.mime || '');
    const isOther = constants.fileUploader.otherMimes.includes(file.mime || '');

    if (isImage) {
      return {
        url: file.signed_url || file.url,
        size: 'cover'
      };
    }

    if (isVideo || isAudio || isOther) {
      return {
        url: constants.fileUploader.icons[file.mime || ''],
        size: '50%'
      };
    }

    return {
      url: constants.fileUploader.icons['application/pdf'],
      size: '50%'
    };
  };

  return (
    <>
      <div style={{ height: spacing }} />

      {!isRecorded && <Line />}

      <div style={{ height: lineSpacing }} />

      <Center horizontal="left" vertical="top" column>
        <Center horizontal="left" vertical="center" gap={gapSpacing}>
          {!isRecorded && (
            <Icon icon={FileIcon} size="24px" color={theme.colors.Primary} />
          )}
          <BigText isRecorded={isRecorded}>Material Complementario</BigText>
        </Center>

        {!!isRecorded ? (
          <>
            <div style={{ height: 14 }} />
            <Line />
            <div style={{ height: 26 }} />
          </>
        ) : (
          <div style={{ height: titleSpacing }} />
        )}


        {session?.complementary_material_description && (() => {
          try {
            const parsed = JSON.parse(session.complementary_material_description);
            const jsonStr = parsed?.jsonStr || session.complementary_material_description;
            const content = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;

            return (
              <>
                <Description>
                  <RemirrorPreview content={content} />
                </Description>
                <div style={{ height: 20 }} />
              </>
            );
          } catch (e) {
            // Fallback to plain text if parsing fails
            return (
              <>
                <Description>
                  {session.complementary_material_description}
                </Description>
                <div style={{ height: 20 }} />
              </>
            );
          }
        })()}

        {session?.complementary_material_files && session.complementary_material_files.length > 0 && (
          <FilesContainer>
            <FilesList>
              {session.complementary_material_files.map((file, index) => {
                const fileName = file?.name || file?.original_name;
                const ext = fileName?.slice((fileName?.lastIndexOf(".") - 1 >>> 0) + 2);
                let lastIndex = fileName?.lastIndexOf('.');
                let name = lastIndex !== -1 ? fileName?.substring(0, lastIndex) : fileName;
                const isImage = constants.fileUploader.imagesMimes.includes(file.mime || '');
                const isVideo = constants.fileUploader.videoMimes.includes(file.mime || '');
                const isPdf = file.mime === 'application/pdf';
                const thumbnail = getFileThumbnail(file);

                return (
                  <FileCard
                    key={slugify(`${file?.name}-${index}`, { lower: true })}
                    onClick={() => handleFileClick(file, index)}
                  >
                    <FileInfo>
                      <FileThumbnail
                        $backgroundUrl={thumbnail.url}
                        $backgroundSize={thumbnail.size}
                      />
                      <FileDetails>
                        <FileName>
                          {name}.{ext}
                        </FileName>
                        <FileType>
                          {isImage && 'Imagen'}
                          {isVideo && 'Video'}
                          {isPdf && 'PDF'}
                          {!isImage && !isVideo && !isPdf && 'Archivo'}
                        </FileType>
                      </FileDetails>
                    </FileInfo>

                    {session.complementary_material_downloadable && (
                      <DownloadButton
                        $isMobile={isMobile}
                        onClick={(e) => {
                          e.stopPropagation();
                          const link = document.createElement('a');
                          link.href = file.signed_url || file.url || '';
                          link.download = fileName || 'download';
                          link.target = '_blank';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        {isMobile ? (
                          <Icon icon={DownloadIcon} size="20px" color="white" />
                        ) : (
                          'Descargar'
                        )}
                      </DownloadButton>
                    )}
                  </FileCard>
                );
              })}
            </FilesList>
          </FilesContainer>
        )}
      </Center>

      <MediaModal
        files={session.complementary_material_files.filter(file =>
          constants.fileUploader.imagesMimes.includes(file.mime || '') ||
          constants.fileUploader.videoMimes.includes(file.mime || '')
        )}
        initialIndex={selectedFileIndex}
        visibleModal={visibleModal}
        isDownloadable={session.complementary_material_downloadable}
      />
    </>
  );
};

const state = ({ modalStore }) => ({ ...modalStore });

export default connect(state)(ComplementaryFiles);
